/**
 * Intelligence Backbone — /companies route.
 *
 * Four foundational datasets (Companies · Topics · Markets · Segments) driven
 * by the Beyond Activ taxonomy. Presented as:
 *  - Priority Living Records hero (3-col compact recap)
 *  - Tabbed exploration for each dataset
 *
 * All data is parallel-fetched via React Query. Priority items get a subtle
 * sky-blue ring so they read as "backbone" at a glance without becoming noisy.
 */
import { useMemo, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2, Wrench, Heart, Zap, Globe, Star, MapPin,
  Sparkles, Layers, Tag, ChevronRight, ExternalLink, Search,
} from "lucide-react";
import PageHeader from "@/layout/PageHeader";
import { Input } from "@/components/ui/input";
import { fetchers, qk, prefetchEntityArticles } from "@/lib/queryClient";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

// ---------- Design tokens (kept local for readability) ----------
const ACCENT = "#0FA9DB";
const ACCENT_SOFT = "#DBEDF7";
const FG = "#0F1B2D";
const FG_MUTED = "#3D4B62";
const FG_SUBTLE = "#5B6B82";
const BORDER = "#E5E9F0";
const SURFACE = "#FFFFFF";
const SURFACE_ALT = "#F7FAFC";

// ---------- Taxonomy: 5 company categories with lucide icons ----------
const CATEGORIES = [
  { key: "operators", label: "Operators and Platforms",                        Icon: Building2 },
  { key: "suppliers", label: "Suppliers, Equipment and Technology",            Icon: Wrench },
  { key: "wellness",  label: "Wellness, Recovery and Hospitality-Adjacent",    Icon: Heart },
  { key: "boutique",  label: "Boutique, Franchise and Specialist Fitness",     Icon: Zap },
  { key: "corporate", label: "Corporate Wellness, Community and Consumer",     Icon: Globe },
];

// ---------- Segment bucket → icon ----------
const SEGMENT_BUCKET_ICON = {
  operator:   Building2,
  equipment:  Wrench,
  technology: Layers,
  services:   Sparkles,
  corporate:  Globe,
  other:      Tag,
};

// ---------- Priority company slugs (the 15 backbone names) ----------
const PRIORITY_COMPANY_SLUGS = new Set([
  "planet-fitness", "puregym", "basic-fit", "life-time", "leejam-sports",
  "technogym", "abc-fitness", "matrix-fitness", "oura", "whoop",
  "wellhub", "xponential-fitness", "lifefit-group", "rsg-group", "smartfit",
]);

// Closed canonical region list — mirror of the DB `companies_region_check`
// constraint. Ordered per the UX brief. NO flags, NO emoji.
const ALLOWED_REGIONS = [
  "Global", "North America", "Europe", "Asia", "Oceania",
  "Middle East", "Africa", "Latin America",
];

// The full 56 canonical topics rendered in the PriorityHero widget, in the
// exact editorial order (mirrors backend CANONICAL_TOPICS and DB
// `topics.display_order`). article_count is displayed as a badge but does
// not affect ordering. Turn CC · 32 refresh.
const ALL_TOPIC_SLUGS = [
  "health", "fitness", "wellness", "wellbeing", "longevity",
  "active-lifestyle", "exercise", "movement", "performance", "recovery",
  "prevention", "nutrition", "sleep", "mindfulness", "healthy-ageing",
  "mental-health", "sports", "hospitality", "experiences", "community",
  "membership", "engagement", "retention", "personalisation", "coaching",
  "operators", "clubs", "gyms", "studios", "boutiques",
  "franchising", "facilities", "equipment", "technology", "digital-fitness",
  "connected-fitness", "wearables", "ai", "diagnostics", "biohacking",
  "healthtech", "fittech", "welltech", "investment", "m-and-a",
  "expansion", "consumer-experience", "corporate-wellness", "human-performance",
  "pilates", "yoga", "barre", "hiit", "strength-training",
  "functional-training", "group-exercise",
];

// ---------- Companies table grid template ----------
// Widths MUST stay in sync between header row (in the sticky container)
// and body rows (in <CompanyGridRow>).
// Company · Website · Segment · Topics · BizType · Priority · Score · BAR
const COMPANIES_GRID_COLS =
  "grid-cols-[220px_60px_minmax(200px,1fr)_minmax(200px,1fr)_130px_110px_100px_180px]";
const COMPANIES_MIN_W = "min-w-[1200px]";

// Priority chip palette — applied ONLY to the canonical semantic set
// (Critical/High/Medium/Low). Any other DB value (e.g. verbatim "Tier 1"
// / "Tier 3" from the source PDF) falls back to the neutral chip style
// so we never invent a semantic mapping we can't defend.
const PRIORITY_CHIP_STYLE = {
  Critical: "bg-red-500/10    text-red-500    border-red-500/30",
  High:     "bg-amber-500/10  text-amber-500  border-amber-500/30",
  Medium:   "bg-slate-500/10  text-slate-500  border-slate-500/30",
  Low:      "bg-zinc-500/10   text-zinc-500   border-zinc-500/30",
};

const faviconFor = (website, name) => {
  if (website) {
    try {
      const host = new URL(website).hostname;
      return `https://icons.duckduckgo.com/ip3/${host}.ico`;
    } catch { /* fall through */ }
  }
  const guess = `${(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "")}.com`;
  return `https://icons.duckduckgo.com/ip3/${guess}.ico`;
};

// ============================================================================
// Small building-block components
// ============================================================================

function PriorityChip({ label = "PRIORITY" }) {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
      style={{ color: ACCENT, background: ACCENT_SOFT }}
    >
      <Star className="h-2.5 w-2.5" strokeWidth={2.5} fill={ACCENT} />
      {label}
    </span>
  );
}

function TierChip({ tier, sourceTier }) {
  // Prefer sourceTier text ("tier_1"/"tier_2"/"tier_3") — authoritative.
  // Fall back to int tier only when sourceTier is not set.
  let n = null;
  if (sourceTier && /^tier_[123]$/.test(sourceTier)) {
    n = Number(sourceTier.split("_")[1]);
  } else if (tier && [1, 2, 3].includes(Number(tier))) {
    n = Number(tier);
  }
  if (!n) return null;

  const palette = {
    1: { color: "#0B7CBF", bg: "#DBEDF7", border: "#B7DBEE" },   // sky (priority)
    2: { color: "#4B5563", bg: "#F1F5F9", border: "#E2E8F0" },   // slate
    3: { color: "#78716C", bg: "#F5F5F4", border: "#E7E5E4" },   // stone
  }[n];

  return (
    <span
      className="mono text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded"
      style={{ color: palette.color, background: palette.bg, border: `1px solid ${palette.border}` }}
      data-testid={`tier-chip-tier-${n}`}
    >
      Tier {n}
    </span>
  );
}

// Turn F Phase 2b — pill for auto-created companies within their first 7 days.
// Server computes `is_new`, so the frontend just renders when true.
function NewBadge() {
  return (
    <span
      data-testid="company-new-badge"
      className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full"
      style={{ color: "#FFFFFF", background: ACCENT, border: `1px solid ${ACCENT}` }}
      title="Auto-created within the last 7 days"
    >
      NEW
    </span>
  );
}

function CompanyCard({ c, priority, onClick, onHover }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onHover}
      onFocus={onHover}
      data-testid={`bbcompany-${c.slug}`}
      className="text-left w-full transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2"
      style={{
        background: SURFACE,
        border: `${priority ? "1.5" : "1"}px solid ${priority ? ACCENT : BORDER}`,
        borderRadius: 10,
        padding: 14,
        boxShadow: priority ? "0 1px 0 0 rgba(15,169,219,0.06)" : "none",
        cursor: "pointer",
      }}
    >
      <div className="flex items-start gap-3">
        <img
          src={faviconFor(c.website, c.name)}
          alt=""
          width={28} height={28}
          className="rounded shrink-0"
          style={{ background: SURFACE_ALT, border: `1px solid ${BORDER}` }}
          onError={(e) => { e.currentTarget.style.opacity = 0.25; }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 min-w-0">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate" style={{ color: FG }}>
                {c.name}
              </div>
              {c.primary_market && (
                <div className="text-[11px] mt-0.5 truncate" style={{ color: FG_SUBTLE }}>
                  {c.primary_market}
                </div>
              )}
            </div>
            {priority && <Star className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} fill={ACCENT} color={ACCENT} />}
          </div>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <TierChip tier={c.tier} sourceTier={c.source_tier} />
            {c.is_new && <NewBadge />}
            {c.region ? (
              <span className="mono text-[10px] px-1.5 py-0.5 rounded"
                    style={{ color: FG_MUTED, background: SURFACE_ALT, border: `1px solid ${BORDER}` }}>
                {c.region}
              </span>
            ) : (
              (c.regions || []).slice(0, 2).map((r) => (
                <span key={r} className="mono text-[10px] px-1.5 py-0.5 rounded"
                      style={{ color: FG_MUTED, background: SURFACE_ALT, border: `1px solid ${BORDER}` }}>
                  {r}
                </span>
              ))
            )}
          </div>
          {(c.content_types || []).length > 0 && (
            <div
              className="flex items-center gap-1 mt-1.5 flex-wrap"
              data-testid={`bbcompany-${c.slug}-content-types`}
            >
              {(c.content_types || []).map((ct) => (
                <span
                  key={ct}
                  className="mono text-[10px] px-1.5 py-0.5 rounded"
                  style={{ color: FG_MUTED, background: SURFACE_ALT, border: `1px solid ${BORDER}` }}
                >
                  {ct}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

function TopicCard({ t, highlightRef, onClick, onHover }) {
  const priority = t.is_priority;
  return (
    <button
      type="button"
      ref={highlightRef}
      onClick={onClick}
      onMouseEnter={onHover}
      onFocus={onHover}
      data-testid={`bbtopic-${t.slug}`}
      className="hm-flash-target text-left w-full transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2"
      style={{
        background: SURFACE,
        border: `${priority ? "1.5" : "1"}px solid ${priority ? ACCENT : BORDER}`,
        borderRadius: 10,
        padding: 14,
        position: "relative",
        cursor: "pointer",
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold" style={{ color: FG }}>{t.name}</div>
        {priority && <PriorityChip />}
      </div>
      {t.description && (
        <div className="text-[12px] mt-2 line-clamp-2" style={{ color: FG_MUTED }}>
          {t.description}
        </div>
      )}
      <div className="text-[11px] mt-3 mono uppercase tracking-widest" style={{ color: FG_SUBTLE }}>
        {t.article_count} article{t.article_count === 1 ? "" : "s"}
      </div>
    </button>
  );
}

function MarketCard({ m, wide = false, highlightRef, onClick, onHover }) {
  const priority = m.is_priority;
  return (
    <button
      type="button"
      ref={highlightRef}
      onClick={onClick}
      onMouseEnter={onHover}
      onFocus={onHover}
      data-testid={`bbmarket-${m.slug}`}
      className="hm-flash-target text-left w-full transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2"
      style={{
        background: SURFACE,
        border: `${priority ? "1.5" : "1"}px solid ${priority ? ACCENT : BORDER}`,
        borderRadius: 10,
        padding: wide ? 16 : 12,
        display: "flex",
        alignItems: "center",
        gap: wide ? 14 : 10,
        cursor: "pointer",
      }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-sm font-semibold truncate" style={{ color: FG }}>{m.name}</div>
          {priority && <PriorityChip />}
        </div>
        {m.parent_slug && (
          <div className="text-[11px] mt-0.5" style={{ color: FG_SUBTLE }}>
            {m.parent_slug.replace(/-/g, " ")}
          </div>
        )}
        {wide && (
          <div className="text-[11px] mt-1 mono uppercase tracking-widest" style={{ color: FG_SUBTLE }}>
            {m.company_count} compan{m.company_count === 1 ? "y" : "ies"}
          </div>
        )}
      </div>
    </button>
  );
}

function SegmentCard({ s }) {
  const Icon = SEGMENT_BUCKET_ICON[s.bucket] || Tag;
  return (
    <div
      data-testid={`bbsegment-${s.slug}`}
      style={{
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        padding: 14,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 flex items-center justify-center rounded-md"
          style={{ width: 32, height: 32, background: ACCENT_SOFT, color: ACCENT }}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" style={{ color: FG }}>{s.name}</div>
          <div className="text-[11px] mt-1.5 mono uppercase tracking-widest" style={{ color: FG_SUBTLE }}>
            {s.company_count} compan{s.company_count === 1 ? "y" : "ies"}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Priority Living Records hero
// ============================================================================

/**
 * Uniform row used across the 3 hero columns so their vertical rhythm matches
 * exactly. Left slot = 20px visual anchor (favicon / icon / flag). Label fills
 * the middle. Meta lives on the right and stays discrete. Chevron reveals on
 * hover to signal "click → jump to".
 */
function HeroRow({ testId, leftSlot, label, meta, onClick }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className="hero-row group w-full text-left flex items-center gap-3 px-2 rounded-md transition-colors hover:bg-white"
        style={{ height: 36 }}
      >
        <span className="shrink-0 flex items-center justify-center" style={{ width: 20, height: 20 }}>
          {leftSlot}
        </span>
        <span className="text-sm font-semibold truncate flex-1 min-w-0" style={{ color: FG }}>
          {label}
        </span>
        {meta ? (
          <span
            className="mono text-[11px] shrink-0 tabular-nums"
            style={{ color: FG_SUBTLE }}
          >
            {meta}
          </span>
        ) : null}
        <ChevronRight
          className="h-3.5 w-3.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: FG_SUBTLE }}
          aria-hidden
        />
      </button>
    </li>
  );
}

function HeroColumnHeader({ children, align = "left" }) {
  return (
    <div
      className={`text-[11px] uppercase tracking-wider font-semibold mb-4 px-2 ${align === "center" ? "text-center" : ""}`}
      style={{ color: FG_MUTED }}
    >
      {children}
    </div>
  );
}

function PriorityHero({ companies, topics, markets, onJump }) {
  const priorityCompanies = useMemo(
    // Priority featured companies — user-curated list, sourced from
    // `is_priority_featured` boolean in the DB. Full list (no top-15 slice),
    // ordered alphabetically by name. Uses the un-filtered feed so tier /
    // region chips do not shrink the widget.
    () => (companies || [])
      .filter((c) => c.is_priority_featured)
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [companies],
  );
  const priorityTopics = useMemo(
    // Topics widget: FIXED editorial order (33 canonical slugs).
    // article_count is a badge; it does not affect ordering.
    () => {
      const bySlug = new Map((topics || []).map((t) => [t.slug, t]));
      return ALL_TOPIC_SLUGS
        .map((slug) => bySlug.get(slug))
        .filter(Boolean);
    },
    [topics],
  );
  const priorityMarkets = useMemo(
    // Top Regions widget: only the 8 canonical regions (kind === "region"),
    // ordered by is_priority then company_count. No countries.
    () => (markets || [])
      .filter((m) => m.kind === "region")
      .sort((a, b) => (b.is_priority - a.is_priority) || (b.company_count - a.company_count))
      .slice(0, 10),
    [markets],
  );

  return (
    <div
      className="mb-8"
      data-testid="priority-hero"
      style={{
        background: SURFACE_ALT,
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        padding: 32,
      }}
    >
      <div className="flex items-baseline justify-between gap-4 mb-6">
        <div className="flex items-center gap-2.5">
          <Star className="h-4 w-4" strokeWidth={2.5} fill={ACCENT} color={ACCENT} />
          <div className="text-base font-semibold" style={{ color: FG }}>
            Priority Living Records
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-x-12 gap-y-6 items-stretch">
        {/* ============= Companies column ============= */}
        <div className="flex flex-col">
          <HeroColumnHeader>Priority Companies ({priorityCompanies.length})</HeroColumnHeader>
          <ol
            data-testid="hero-companies"
            className="overflow-y-auto pr-1"
            style={{ maxHeight: 520 }}
          >
            {priorityCompanies.map((c) => (
              <HeroRow
                key={c.slug}
                testId={`hero-company-${c.slug}`}
                leftSlot={
                  <img
                    src={faviconFor(c.website, c.name)}
                    alt="" width={18} height={18}
                    className="rounded-sm"
                    style={{ background: SURFACE, border: `1px solid ${BORDER}` }}
                    onError={(e) => { e.currentTarget.style.opacity = 0.25; }}
                  />
                }
                label={c.name}
                meta={c.tier ? `T${c.tier}` : null}
                onClick={() => onJump("companies", `bbcompany-${c.slug}`)}
              />
            ))}
          </ol>
        </div>

        {/* ============= Topics column (chips, centered) =============
             Header aligned top with siblings, chips block centered both
             horizontally (justify-center on flex-wrap) and vertically
             (flex-1 wrapper). Priority is signaled by a discrete sky dot
             next to the label. */}
        <div className="flex flex-col">
          <HeroColumnHeader align="center">Topics ({priorityTopics.length})</HeroColumnHeader>
          <div className="flex flex-wrap gap-2 justify-center px-2" data-testid="hero-topics">
            {priorityTopics.map((t) => (
              <button
                key={t.slug}
                type="button"
                onClick={() => onJump("topics", `bbtopic-${t.slug}`)}
                data-testid={`hero-topic-${t.slug}`}
                title={t.description || ""}
                className="inline-flex items-center gap-2 text-[13px] font-medium rounded-md transition-colors hover:bg-hm-bg-1"
                style={{
                  color: FG,
                  background: SURFACE,
                  border: `1px solid ${BORDER}`,
                  padding: "7px 12px",
                  lineHeight: 1.1,
                }}
              >
                <span
                  className="rounded-full shrink-0"
                  style={{ width: 5, height: 5, background: ACCENT }}
                  aria-hidden
                />
                {t.name}
                <span
                  className="mono text-[10px] tabular-nums"
                  style={{ color: FG_SUBTLE }}
                  data-testid={`hero-topic-count-${t.slug}`}
                >
                  {t.article_count ?? 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ============= Regions column ============= */}
        <div className="flex flex-col">
          <HeroColumnHeader>Top {priorityMarkets.length} Regions</HeroColumnHeader>
          <ol data-testid="hero-markets">
            {priorityMarkets.map((m) => (
              <HeroRow
                key={m.slug}
                testId={`hero-market-${m.slug}`}
                label={m.name}
                meta={m.kind}
                onClick={() => onJump("markets", `bbmarket-${m.slug}`)}
              />
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Skeleton (contextual, matches the final layout)
// ============================================================================
function BackboneSkeleton() {
  return (
    <div data-testid="skeleton-backbone">
      <div className="mb-8 p-6 rounded-xl" style={{ background: SURFACE_ALT, border: `1px solid ${BORDER}` }}>
        <div className="flex items-center gap-2 mb-5"><Skeleton className="h-3 w-40" /></div>
        <div className="grid grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-2.5 w-20" />
              {Array.from({ length: 8 }).map((__, j) => (
                <Skeleton key={j} className="h-4 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================
// ---------- Priority (source_priority) closed set ----------
// Turn BQ — Priority is now sourced from PDF col[20] `Source Priority`
// (Critical / High / Medium / Low), backfilled into `companies.source_priority`.
// The legacy `monitoring_priority` column (with "Tier 1" / "Tier 3" contamination)
// is no longer read here. NULL / anything outside the closed set → TBD bucket.
const PRIORITY_FILTER_SET = ["Critical", "High", "Medium", "Low"];

function priorityBucket(c) {
  const v = (c?.source_priority || "").trim();
  return PRIORITY_FILTER_SET.includes(v) ? v : "TBD";
}

// Score chip color — legend for the Priority Score column.
function scoreColor(score) {
  if (score == null) return "text-hm-fg-subtle italic";
  if (score >= 80) return "text-emerald-600 font-semibold";
  if (score >= 60) return "text-amber-600  font-semibold";
  return "text-slate-500 font-semibold";
}

// Beyond Activ relationship — the DB stores a `;`-delimited list ingested
// verbatim from the Tier 1/2 PDF (Turn BD). Turn BP normalised it into a
// canonical `text[]` column (`beyond_activ_relationships`) so we can render
// one chip per value without any client-side split-and-guess. Colouring is
// deliberately neutral: the PDF text is the source of truth, and we don't
// invent semantic mappings on top of it.
function relationshipsOf(c) {
  const arr = Array.isArray(c.beyond_activ_relationships)
    ? c.beyond_activ_relationships
    : [];
  if (arr.length > 0) return arr;
  // Legacy retro-compat: if the array column is empty but the original
  // singular text field has content, split on ';' or ',' on the fly.
  const raw = c.beyond_activ_relationship;
  if (!raw || typeof raw !== "string") return [];
  return raw.split(/[;,]/).map((s) => s.trim()).filter(Boolean);
}

// Little inline TBD placeholder — used for every empty cell so the user can
// see at a glance what's missing without wondering if the row is broken.
function Tbd() {
  return (
    <span className="text-hm-fg-subtle italic text-sm" data-testid="cell-tbd">
      TBD
    </span>
  );
}


export default function IntelligenceBackbonePage({ hideHeader = false, externalQ } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tierParam = searchParams.get("tier");
  const activeTier =
    tierParam === "2" ? "tier_2" :
    tierParam === "3" ? "tier_3" :
                        "tier_1";

  const setActiveTier = (t) => {
    const num = t === "tier_2" ? "2" : t === "tier_3" ? "3" : "1";
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.set("tier", num);
      return next;
    });
  };

  // Local filter state (client-side — data is <1k rows, plenty fast).
  const [regionFilter,   setRegionFilter]   = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  // Search is now owned by the Universe parent when embedded; fall back to
  // a local input only if this page is rendered standalone (no parent shell).
  const [localQ, setLocalQ] = useState("");
  const q = externalQ !== undefined ? externalQ : localQ;
  const setQ = externalQ !== undefined ? () => {} : setLocalQ;

  const rootRef = useRef(null);
  const navigate = useNavigate();

  const [companiesQ, topicsQ, marketsQ] = useQueries({
    queries: [
      { queryKey: qk.companies(), queryFn: () => fetchers.companies({ limit: 1000 }) },
      { queryKey: qk.topics(),    queryFn: fetchers.topics },
      { queryKey: qk.markets(),   queryFn: fetchers.markets },
    ],
  });

  const isPending = companiesQ.isPending || topicsQ.isPending || marketsQ.isPending;
  const allCompanies = companiesQ.data?.data || [];
  const topics    = topicsQ.data?.data    || [];
  const markets   = marketsQ.data?.data   || [];

  // ---------- Tier tab counters (from ALL companies, not filtered) ----------
  const tierCount = (tier) =>
    allCompanies.filter((c) =>
      c.source_tier
        ? c.source_tier === tier
        : Number(c.tier) === (tier === "tier_1" ? 1 : tier === "tier_2" ? 2 : 3),
    ).length;

  // ---------- Filtered rows for the active tier ----------
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return allCompanies.filter((c) => {
      // Tier gate — active tier only.
      const tierOk = c.source_tier
        ? c.source_tier === activeTier
        : Number(c.tier) === (activeTier === "tier_1" ? 1 : activeTier === "tier_2" ? 2 : 3);
      if (!tierOk) return false;
      if (regionFilter !== "all" && (c.region || "").trim() !== regionFilter) return false;
      if (priorityFilter !== "all" && priorityBucket(c) !== priorityFilter) return false;
      if (ql) {
        const hay = `${c.name || ""} ${c.website || ""}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    }).sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" }),
    );
  }, [allCompanies, activeTier, regionFilter, priorityFilter, q]);

  // For counter chips inside filters — respect the tier gate only.
  const tierCompanies = useMemo(
    () => allCompanies.filter((c) => (c.source_tier
      ? c.source_tier === activeTier
      : Number(c.tier) === (activeTier === "tier_1" ? 1 : activeTier === "tier_2" ? 2 : 3))),
    [allCompanies, activeTier],
  );

  const openCompany = (slug) => navigate(`/companies/${slug}`);

  const TIER_TABS = [
    { key: "tier_1", label: "Tier 1", count: tierCount("tier_1") },
    { key: "tier_2", label: "Tier 2", count: tierCount("tier_2") },
    { key: "tier_3", label: "Tier 3", count: tierCount("tier_3") },
  ];

  return (
    <div ref={rootRef} data-testid="intelligence-backbone-page">
      {!hideHeader && (
        <PageHeader
          title="Intelligence Backbone"
          badge="Live"
          subtitle="The foundational company, topic, market and segment records powering Hail Mary."
        />
      )}

      {isPending ? (
        <BackboneSkeleton />
      ) : (
        <>
          <PriorityHero
            companies={allCompanies} topics={topics} markets={markets}
            onJump={() => { /* tabs removed; nav handled inline */ }}
          />

          {/* Sub-tab bar — Tier 1 · Tier 2 · Tier 3 (URL-driven via ?tier=) */}
          <div
            className="flex items-center gap-1 mb-6 p-1 rounded-lg w-fit"
            data-testid="backbone-tabs"
            style={{ background: SURFACE_ALT, border: `1px solid ${BORDER}` }}
          >
            {TIER_TABS.map(({ key, label, count }) => {
              const active = activeTier === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTier(key)}
                  data-testid={`backbone-tab-${key}`}
                  className="text-sm font-medium px-3.5 py-1.5 rounded-md transition-all"
                  style={{
                    color: active ? FG : FG_MUTED,
                    background: active ? SURFACE : "transparent",
                    border: `1px solid ${active ? BORDER : "transparent"}`,
                    boxShadow: active ? "0 1px 0 0 rgba(15,27,45,0.03)" : "none",
                  }}
                >
                  {label}
                  <span className="mono text-[11px] ml-1.5" style={{ color: FG_SUBTLE }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Sticky container — filter bar + column-header row bundled
              so they never separate and no gap lets scrolling rows
              bleed through. Fully opaque, zero backdrop-blur. */}
          <div
            className="sticky top-[54px] z-20 -mx-8 bg-hm-bg"
            data-testid="companies-sticky"
          >
            <div
              className="px-8 pt-3 pb-3 space-y-3"
              data-testid="companies-filters"
            >
            {/* Row 1 — Region chips + search + counter */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mr-1">
                Region
              </span>
              <FilterChip
                active={regionFilter === "all"}
                onClick={() => setRegionFilter("all")}
                testid="companies-filter-region-all"
              >
                All <span className="mono text-[10px] ml-1 opacity-70">{tierCompanies.length}</span>
              </FilterChip>
              {ALLOWED_REGIONS.map((r) => {
                const n = tierCompanies.filter((c) => (c.region || "").trim() === r).length;
                if (n === 0) return null;
                return (
                  <FilterChip
                    key={r}
                    active={regionFilter === r}
                    onClick={() => setRegionFilter(r)}
                    testid={`companies-filter-region-${r.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {r} <span className="mono text-[10px] ml-1 opacity-70">{n}</span>
                  </FilterChip>
                );
              })}

              <div className="ml-auto flex items-center gap-3">
                <span
                  className="text-xs text-hm-fg-subtle whitespace-nowrap"
                  data-testid="companies-count"
                >
                  Showing {filtered.length} of {tierCompanies.length} companies
                </span>
              </div>
            </div>

            {/* Row 2 — Priority chips (mapped to source_priority ← PDF col[20]; TBD = NULL) */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mr-1">
                Priority
              </span>
              <FilterChip
                active={priorityFilter === "all"}
                onClick={() => setPriorityFilter("all")}
                testid="companies-filter-priority-all"
              >
                All
              </FilterChip>
              {[...PRIORITY_FILTER_SET, "TBD"].map((p) => {
                const n = tierCompanies.filter((c) => priorityBucket(c) === p).length;
                return (
                  <FilterChip
                    key={p}
                    active={priorityFilter === p}
                    onClick={() => setPriorityFilter(p)}
                    testid={`companies-filter-priority-${p.toLowerCase()}`}
                  >
                    {p} <span className="mono text-[10px] ml-1 opacity-70">{n}</span>
                  </FilterChip>
                );
              })}
            </div>
            </div>

            {/* Column-header row (grid) — attached directly to the
                filter bar so the two never separate visually. */}
            <div className="bg-hm-bg-1 border-y border-hm-border">
              <div className={cn("px-8 grid gap-0 h-10 items-center", COMPANIES_GRID_COLS, COMPANIES_MIN_W)}>
                <div className="px-3 text-[11px] uppercase tracking-wider text-hm-fg-muted font-medium">Company</div>
                <div className="px-3 text-[11px] uppercase tracking-wider text-hm-fg-muted font-medium text-center">Website</div>
                <div className="px-3 text-[11px] uppercase tracking-wider text-hm-fg-muted font-medium">Segment</div>
                <div className="px-3 text-[11px] uppercase tracking-wider text-hm-fg-muted font-medium">Topics</div>
                <div className="px-3 text-[11px] uppercase tracking-wider text-hm-fg-muted font-medium">Business Type</div>
                <div className="px-3 text-[11px] uppercase tracking-wider text-hm-fg-muted font-medium">Priority</div>
                <div className="px-3 text-[11px] uppercase tracking-wider text-hm-fg-muted font-medium text-right">Priority Score</div>
                <div className="px-3 text-[11px] uppercase tracking-wider text-hm-fg-muted font-medium">Beyond Activ</div>
              </div>
            </div>
          </div>

          {/* Body */}
          {filtered.length === 0 ? (
            <div className="mono text-hm-fg-subtle text-center py-12 text-xs uppercase tracking-widest border-b border-hm-border/60 -mx-8">
              No companies match the current filters
            </div>
          ) : (
            <div className="-mx-8" data-testid="companies-table-wrapper">
              <div className={COMPANIES_MIN_W}>
                {filtered.map((c) => (
                  <CompanyGridRow key={c.id} c={c} onOpen={openCompany} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Row + filter chip helpers — kept alongside the page so they inherit the
// existing design tokens without a shared component churn.
// -----------------------------------------------------------------------------

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={cn(
        "text-[12px] font-medium rounded-full px-3 py-1 border transition-all",
        active
          ? "bg-hm-fg text-hm-bg border-hm-fg"
          : "bg-hm-bg text-hm-fg-muted border-hm-border hover:text-hm-fg",
      )}
    >
      {children}
    </button>
  );
}

function CompanyGridRow({ c, onOpen }) {
  const segments = [c.primary_segment, c.secondary_segment, c.tertiary_segment]
    .filter(Boolean);
  const topics = Array.isArray(c.key_topics) ? c.key_topics : [];
  const relationships = relationshipsOf(c);
  const domain = (() => {
    try { return c.website ? new URL(c.website).hostname.replace(/^www\./, "") : null; }
    catch { return null; }
  })();

  return (
    <div
      data-testid={`companies-row-${c.slug}`}
      onMouseEnter={() => prefetchEntityArticles("company", c.slug)}
      className={cn(
        "px-8 grid gap-0 items-start border-b border-hm-border/60 hover:bg-hm-bg-1/60 transition-colors min-h-[52px]",
        COMPANIES_GRID_COLS,
      )}
    >
      {/* Company Name — bold, clickable */}
      <div className="px-3 py-3 min-w-0" title={c.name}>
        <button
          type="button"
          onClick={() => onOpen(c.slug)}
          className="text-hm-fg font-medium text-sm truncate block text-left hover:text-hm-accent transition-colors w-full"
        >
          {c.name}
        </button>
      </div>

      {/* Website — icon */}
      <div className="px-3 py-3 flex items-start justify-center">
        {c.website ? (
          <a
            href={c.website}
            target="_blank"
            rel="noopener noreferrer"
            title={domain || c.website}
            data-testid={`companies-link-${c.slug}`}
            className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-hm-border bg-hm-bg text-hm-fg-muted hover:text-hm-fg hover:border-hm-border-strong transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : <Tbd />}
      </div>

      {/* Segment — one chip per segment level, all visible, wraps naturally.
          `min-w-0` on the cell is critical: without it, CSS grid lets the
          intrinsic chip width bleed into the next column. */}
      <div className="px-3 py-3 min-w-0 overflow-hidden">
        {segments.length === 0 ? <Tbd /> : (
          <div className="flex flex-wrap gap-1.5" data-testid={`companies-segments-${c.slug}`}>
            {segments.map((seg, i) => (
              <span
                key={`${seg}-${i}`}
                className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-hm-border bg-hm-bg-1 text-hm-fg-muted max-w-full whitespace-normal break-words leading-tight"
              >
                {seg}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Topics — all chips visible, wraps naturally, no +N overflow */}
      <div className="px-3 py-3 min-w-0 overflow-hidden">
        {topics.length === 0 ? <Tbd /> : (
          <div className="flex flex-wrap gap-1" data-testid={`companies-topics-${c.slug}`}>
            {topics.map((t) => (
              <span
                key={t}
                className="mono text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border border-hm-border bg-hm-bg-1 text-hm-fg-muted max-w-full whitespace-normal break-words leading-tight"
                title={t}
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Business Type */}
      <div className="px-3 py-3 min-w-0 overflow-hidden" title={c.business_type || ""}>
        {c.business_type ? (
          <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-hm-border bg-hm-bg-1 text-hm-fg-muted max-w-full whitespace-normal break-words leading-tight inline-block">
            {c.business_type}
          </span>
        ) : <Tbd />}
      </div>

      {/* Priority — verbatim `source_priority` from PDF col[20] (Turn BQ).
          Coloured via `PRIORITY_CHIP_STYLE` (Critical/High/Medium/Low).
          NULL → TBD italic (no invention). */}
      <div className="px-3 py-3 min-w-0" title={c.source_priority || ""}>
        {c.source_priority ? (
          <span
            data-testid={`companies-priority-${c.slug}`}
            className={cn(
              "mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border max-w-full whitespace-nowrap inline-block truncate",
              PRIORITY_CHIP_STYLE[c.source_priority] ||
                "border-hm-border bg-hm-bg-1 text-hm-fg-muted",
            )}
          >
            {c.source_priority}
          </span>
        ) : <Tbd />}
      </div>

      {/* Priority Score */}
      <div className="px-3 py-3 text-right">
        {c.priority_score == null ? <Tbd /> : (
          <span
            className={cn("text-sm tabular-nums", scoreColor(c.priority_score))}
            data-testid={`companies-score-${c.slug}`}
          >
            {c.priority_score}
          </span>
        )}
      </div>

      {/* Beyond Activ — one chip per canonical relationship from the PDF */}
      <div className="px-3 py-3 min-w-0">
        {relationships.length === 0 ? <Tbd /> : (
          <div className="flex flex-wrap gap-1" data-testid={`companies-bar-${c.slug}`}>
            {relationships.map((rel) => (
              <span
                key={rel}
                className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-hm-border-strong bg-hm-bg-1 text-hm-fg whitespace-nowrap"
              >
                {rel}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
